import {
  differenceInCalendarDays,
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  format,
  isValid,
  parseISO,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear
} from 'date-fns';
import {
  Box,
  Flex,
  Grid,
  SimpleGrid,
  Spinner,
  Text,
  Tooltip
} from '@chakra-ui/react';
import {
  Badge,
  Button,
  ButtonIcon,
  DatePicker,
  Input,
  Modal,
  OutlineCard,
  useToast
} from '@morressier/alembic';
import { useQuery } from '@tanstack/react-query';
import { ComponentProps, ReactNode, useEffect, useMemo, useState } from 'react';
import type {
  ArticleTypeByRevision,
  CleanAnalyticsResponse,
  ManuscriptCountByDate,
  RevisionCount
} from '@publishing-engine/domain/actions/v1/analytics/analytics-response-types';
import type { SummaryRow } from '@publishing-engine/domain/actions/v1/analytics/analytics-input-types';
import { useWorkspaceAnalyticsOverviewMutation } from 'src/api/mutations';
import {
  fetchWorkspaceAnalyticsConfigs,
  WorkspaceAnalyticsChartConfig,
  WorkspaceAnalyticsConfigResponse,
  WorkspaceAnalyticsRequestField,
  WorkspaceAnalyticsTabConfig
} from 'src/api/queries';
import Footer from 'src/components/Footer';
import { FieldLabel } from 'src/components/Field';
import MultiSelect from 'src/components/MultiSelect';
import Loading from 'src/pages/Status/Loading';
import SingleItem from 'src/layouts/SingleItem';
import { useWorkspace } from '../../SubmissionContexts/Users/userHook';
import { ArticleTypeBarChart } from './charts/ArticleTypeBarChart';
import { DecisionRatioChart } from './charts/DecisionRatioChart';
import { ManuscriptCountLineChart } from './charts/ManuscriptCountLineChart';
import { ManuscriptsReceivedChart } from './charts/ManuscriptsReceivedChart';
import { RevisionRoundLineAreaChart } from './charts/RevisionRoundLineAreaChart';
import { SubmissionOverTimeAreaChart } from './charts/SubmissionOverTimeAreaChart';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExpand,
  faInfoCircle,
  faLockKeyhole
} from '@fortawesome/pro-regular-svg-icons';

type MetricCardData = {
  label: string;
  value: string | number;
  subtext: string;
};

type RequestValues = Record<string, string>;
type AnalyticsRequestSchemaValues = Record<string, string | string[]>;

type AnalyticsStatsResponse = SummaryRow;
type SubmissionTimeBucket = 'day' | 'week' | 'month' | 'quarter' | 'year';

const unwrapAnalyticsPayload = <T,>(value: unknown): T | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const record = value as Record<string, unknown>;

  if ('data' in record && record.data) {
    return record.data as T;
  }

  return value as T;
};

const getConfigNumber = (value: unknown, fallback: number) =>
  typeof value === 'number' && !Number.isNaN(value) ? value : fallback;

const getErrorStatusCode = (error: unknown): number | undefined => {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  const errorRecord = error as Record<string, unknown>;

  if (typeof errorRecord.status === 'number') {
    return errorRecord.status;
  }

  if (typeof errorRecord.statusCode === 'number') {
    return errorRecord.statusCode;
  }

  if ('response' in errorRecord && errorRecord.response) {
    return getErrorStatusCode(errorRecord.response);
  }

  if ('message' in errorRecord && errorRecord.message) {
    return getErrorStatusCode(errorRecord.message);
  }

  return undefined;
};

const formatPercentValue = (value: number) => `${(value * 100).toFixed(1)}%`;

const manuscriptCountLineChartData: ComponentProps<
  typeof ManuscriptCountLineChart
>['data'] = {
  points: [
    { label: 'Jan', count: 0 },
    { label: 'Feb', count: 0 },
    { label: 'Mar', count: 0 }
  ]
};

const manuscriptsReceivedChartData: ComponentProps<
  typeof ManuscriptsReceivedChart
>['data'] = {
  by_revision: [
    { revision: 'R0', count: 0 },
    { revision: 'R1', count: 0 },
    { revision: 'R2', count: 0 },
    { revision: 'R3', count: 0 },
    { revision: 'R4', count: 0 },
    { revision: 'R5', count: 0 }
  ]
};

const articleTypeBarChartData: ComponentProps<
  typeof ArticleTypeBarChart
>['data'] = {
  rows: [
    {
      revision: 'R0',
      Editorial: 0,
      'Article for Peer Review': 0,
      'Protocol for Peer Review': 0
    }
  ],
  series: ['Editorial', 'Article for Peer Review', 'Protocol for Peer Review']
};

type ArticleTypeChartRow = {
  article_type: string;
  type_count: number;
};

const isArticleTypeChartRow = (value: unknown): value is ArticleTypeChartRow =>
  !!value &&
  typeof value === 'object' &&
  typeof (value as ArticleTypeChartRow).article_type === 'string' &&
  typeof (value as ArticleTypeChartRow).type_count === 'number';

const isArticleTypeChartRows = (
  value: unknown
): value is ArticleTypeChartRow[] =>
  Array.isArray(value) && value.every(isArticleTypeChartRow);

const revisionRoundLineAreaChartData: ComponentProps<
  typeof RevisionRoundLineAreaChart
>['data'] = {
  rounds: manuscriptsReceivedChartData.by_revision.map(item => ({
    round: item.revision,
    count: item.count
  }))
};

const decisionRatioChartData: ComponentProps<
  typeof DecisionRatioChart
>['data'] = {
  accept: 0,
  reject: 0,
  revise: 0
};

const normalizeAnalyticsConfigs = (
  config: WorkspaceAnalyticsConfigResponse | null | undefined
) => {
  if (Array.isArray(config)) {
    return config;
  }
  return [];
};

const buildMetricCards = (
  stats: AnalyticsStatsResponse | null
): MetricCardData[] => [
  {
    label: 'Total manuscripts',
    value: getConfigNumber(stats?.total_manuscripts, 0),
    subtext:
      'Total number of submissions across selected journals, including those in progress and with decisions'
  },
  {
    label: 'Total Revisions',
    value: getConfigNumber(stats?.total_revisions, 0),
    subtext: 'Revisions across all manuscripts'
  },
  {
    label: 'In Progress',
    value: getConfigNumber(stats?.manuscripts_in_progress, 0),
    subtext: 'Number of submissions currently in progress'
  },
  {
    label: 'With decisions',
    value:
      getConfigNumber(stats?.manuscripts_with_final_decision, 0),
    subtext:
      'Number of submissions for which a final editorial decision has been made and moved to the archive'
  },
  {
    label: 'Avg. days to decision',
    value: `${getConfigNumber(stats?.average_time_to_decision_days, 0)}d`,
    subtext:
      'Average time taken to reach a decision across all manuscripts with completed decisions'
  },
  {
    label: 'Acceptance rate percentage',
    value: formatPercentValue(getConfigNumber(stats?.acceptance_rate, 0)),
    subtext:
      'Percentage of accepted manuscripts out of total manuscripts with decisions'
  },
  {
    label: 'Rejection rate percentage',
    value: formatPercentValue(getConfigNumber(stats?.rejection_rate, 0)),
    subtext:
      'Percentage of rejected manuscripts out of total manuscripts with decisions'
  },
  {
    label: 'Withdrawal rate percentage',
    value: formatPercentValue(getConfigNumber(stats?.withdrawal_rate, 0)),
    subtext:
      'Percentage of withdrawn manuscripts out of total manuscripts with decisions'
  }
];

const getRevisionRoundResponse = (value: unknown): RevisionCount[] =>
  Array.isArray(value)
    ? (value.filter(
        item =>
          typeof item === 'object' &&
          item !== null &&
          'revision' in item &&
          typeof (item as RevisionCount).revision === 'string' &&
          'revision_count' in item &&
          typeof (item as RevisionCount).revision_count === 'number'
      ) as RevisionCount[])
    : [];

const getArticleTypeByRevisionResponse = (
  value: unknown
): ArticleTypeByRevision => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).filter(([, rows]) =>
      isArticleTypeChartRows(rows)
    )
  ) as ArticleTypeByRevision;
};

const getManuscriptCountByDateResponse = (
  value: unknown
): ManuscriptCountByDate =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as ManuscriptCountByDate)
    : {};

const parseAnalyticsDate = (value: string) => {
  const parsedDate = parseISO(value);

  return isValid(parsedDate) ? parsedDate : null;
};

const formatSubmissionBucketLabel = (
  date: Date,
  bucket: SubmissionTimeBucket
) => {
  switch (bucket) {
    case 'day':
      return format(date, 'dd MMM');
    case 'week':
      return format(date, 'dd MMM');
    case 'month':
      return format(date, 'MMM yyyy');
    case 'quarter':
      return `Q${Math.floor(date.getMonth() / 3) + 1} ${format(date, 'yyyy')}`;
    case 'year':
      return format(date, 'yyyy');
    default:
      return format(date, 'dd MMM yyyy');
  }
};

const formatSubmissionBucketFullLabel = (
  startDate: Date,
  endDate: Date,
  bucket: SubmissionTimeBucket
) => {
  switch (bucket) {
    case 'day':
      return format(startDate, 'dd MMM yyyy');
    case 'week':
      return `${format(startDate, 'dd MMM yyyy')} - ${format(endDate, 'dd MMM yyyy')}`;
    case 'month':
      return format(startDate, 'MMMM yyyy');
    case 'quarter':
      return `Q${Math.floor(startDate.getMonth() / 3) + 1} ${format(startDate, 'yyyy')}`;
    case 'year':
      return format(startDate, 'yyyy');
    default:
      return format(startDate, 'dd MMM yyyy');
  }
};

const getSubmissionTimeBucket = (
  points: Array<{ date: Date }>,
  requestValues?: RequestValues
): SubmissionTimeBucket => {
  const startDate = requestValues?.startDate
    ? parseAnalyticsDate(requestValues.startDate)
    : (points[0]?.date ?? null);
  const endDate = requestValues?.endDate
    ? parseAnalyticsDate(requestValues.endDate)
    : (points[points.length - 1]?.date ?? null);

  if (!startDate || !endDate) {
    return 'month';
  }

  const totalDays = Math.abs(differenceInCalendarDays(endDate, startDate));

  if (totalDays <= 45) {
    return 'day';
  }

  if (totalDays <= 180) {
    return 'week';
  }

  if (totalDays <= 730) {
    return 'month';
  }

  if (totalDays <= 3650) {
    return 'quarter';
  }

  return 'year';
};

const getSubmissionBucketRange = (
  date: Date,
  bucket: SubmissionTimeBucket
): [Date, Date] => {
  switch (bucket) {
    case 'day':
      return [date, date];
    case 'week':
      return [
        startOfWeek(date, { weekStartsOn: 1 }),
        endOfWeek(date, { weekStartsOn: 1 })
      ];
    case 'month':
      return [startOfMonth(date), endOfMonth(date)];
    case 'quarter':
      return [startOfQuarter(date), endOfQuarter(date)];
    case 'year':
      return [startOfYear(date), endOfYear(date)];
    default:
      return [date, date];
  }
};

const aggregateSubmissionOverTimeData = (
  countsByDate: ManuscriptCountByDate,
  requestValues?: RequestValues
) => {
  const parsedEntries = Object.entries(countsByDate)
    .map(([dateKey, count]) => ({
      date: parseAnalyticsDate(dateKey),
      count: Number(count)
    }))
    .filter(
      (
        entry
      ): entry is {
        date: Date;
        count: number;
      } => entry.date !== null && !Number.isNaN(entry.count)
    )
    .sort((left, right) => left.date.getTime() - right.date.getTime());

  const bucket = getSubmissionTimeBucket(parsedEntries, requestValues);
  const groupedPoints = new Map<
    string,
    {
      startDate: Date;
      endDate: Date;
      count: number;
    }
  >();

  parsedEntries.forEach(entry => {
    const [startDate, endDate] = getSubmissionBucketRange(entry.date, bucket);
    const key = startDate.toISOString();
    const currentValue = groupedPoints.get(key);

    if (currentValue) {
      currentValue.count += entry.count;
      return;
    }

    groupedPoints.set(key, {
      startDate,
      endDate,
      count: entry.count
    });
  });

  return {
    bucket,
    points: Array.from(groupedPoints.values()).map(item => ({
      label: formatSubmissionBucketLabel(item.startDate, bucket),
      fullLabel: formatSubmissionBucketFullLabel(
        item.startDate,
        item.endDate,
        bucket
      ),
      count: item.count
    }))
  };
};

const getSubmissionOverTimeMeta = (
  value: unknown,
  requestValues?: RequestValues
) => {
  const countsByDate = getManuscriptCountByDateResponse(value);
  const { bucket, points } = aggregateSubmissionOverTimeData(
    countsByDate,
    requestValues
  );

  if (!points.length) {
    return undefined;
  }

  const bucketLabelByType: Record<SubmissionTimeBucket, string> = {
    day: 'Daily',
    week: 'Weekly',
    month: 'Monthly',
    quarter: 'Quarterly',
    year: 'Yearly'
  };

  return `${bucketLabelByType[bucket]} view`;
};

const getSubmissionOverTimeXAxisLabel = (
  value: unknown,
  requestValues?: RequestValues
) => {
  const countsByDate = getManuscriptCountByDateResponse(value);
  const { bucket } = aggregateSubmissionOverTimeData(
    countsByDate,
    requestValues
  );

  const axisLabelByType: Record<SubmissionTimeBucket, string> = {
    day: 'Day',
    week: 'Week',
    month: 'Month',
    quarter: 'Quarter',
    year: 'Year'
  };

  return axisLabelByType[bucket];
};

const getRevisionRoundBarData = (
  value: unknown
): ComponentProps<typeof ManuscriptsReceivedChart>['data'] => {
  const revisionRows = getRevisionRoundResponse(value);

  return {
    by_revision: revisionRows.length
      ? revisionRows.map(item => ({
          revision: item.revision,
          count: item.revision_count
        }))
      : manuscriptsReceivedChartData.by_revision
  };
};

const getRevisionRoundLineAreaData = (
  value: unknown
): ComponentProps<typeof RevisionRoundLineAreaChart>['data'] => {
  const items = getRevisionRoundResponse(value);

  return {
    rounds: items.length
      ? items.map(item => ({
          round: item.revision,
          count: item.revision_count
        }))
      : revisionRoundLineAreaChartData.rounds
  };
};

const getManuscriptCountLineData = (
  value: unknown
): ComponentProps<typeof ManuscriptCountLineChart>['data'] => {
  const countsByDate = getManuscriptCountByDateResponse(value);
  const points = Object.entries(countsByDate).map(([label, count]) => ({
    label,
    count
  }));

  return {
    points: points.length ? points : manuscriptCountLineChartData.points
  };
};

const getSubmissionOverTimeAreaData = (
  value: unknown,
  requestValues?: RequestValues
): ComponentProps<typeof SubmissionOverTimeAreaChart>['data'] => {
  const countsByDate = getManuscriptCountByDateResponse(value);
  const aggregatedData = aggregateSubmissionOverTimeData(
    countsByDate,
    requestValues
  );

  return {
    points: aggregatedData.points.length
      ? aggregatedData.points
      : manuscriptCountLineChartData.points.map(point => ({
          ...point,
          fullLabel: point.label
        }))
  };
};

const getDecisionRatioData = (
  value: unknown
): ComponentProps<typeof DecisionRatioChart>['data'] => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return decisionRatioChartData;
  }

  const summary = value as SummaryRow;
  const total = getConfigNumber(summary.manuscripts_with_final_decision, 0);

  return {
    accept: Math.round(total * getConfigNumber(summary.acceptance_rate, 0)),
    reject: Math.round(total * getConfigNumber(summary.rejection_rate, 0)),
    revise: 0
  };
};

const getSubmissionPipelineBarData = (
  value: unknown
): ComponentProps<typeof ManuscriptsReceivedChart>['data'] => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return manuscriptsReceivedChartData;
  }

  const summary = value as SummaryRow;

  return {
    by_revision: [
      {
        revision: 'Total',
        count: getConfigNumber(summary.total_submissions, 0)
      },
      {
        revision: 'In Progress',
        count: getConfigNumber(summary.manuscripts_in_progress, 0)
      },
      {
        revision: 'Decision',
        count: getConfigNumber(summary.manuscripts_with_final_decision, 0)
      }
    ]
  };
};

const getArticleTypeBarData = (
  value: unknown
): ComponentProps<typeof ArticleTypeBarChart>['data'] => {
  const articleTypeByRevision = getArticleTypeByRevisionResponse(value);
  const series = new Set<string>();

  Object.values(articleTypeByRevision).forEach(typeRows => {
    typeRows.forEach(typeItem => {
      series.add(typeItem.article_type);
    });
  });

  const rows = Object.entries(articleTypeByRevision).map(
    ([revision, typeRows]) => {
      const row: Record<string, string | number> = {
        revision
      };

      Array.from(series).forEach(seriesKey => {
        row[seriesKey] = 0;
      });

      typeRows.forEach(typeItem => {
        row[typeItem.article_type] = typeItem.type_count;
      });

      return row;
    }
  );

  return {
    rows: rows.length ? rows : articleTypeBarChartData.rows,
    series: series.size ? Array.from(series) : articleTypeBarChartData.series
  };
};

const AnalyticsMetricCard = ({ label, value, subtext }: MetricCardData) => (
  <OutlineCard
    h={{ base: 'auto', md: '124px' }}
    minH={{ base: '120px', md: '124px' }}
    maxW={{ base: '100%', xl: '168px' }}
    w="100%"
  >
    <Flex
      direction="column"
      p={{ base: 4, md: 4 }}
      position="relative"
      h="100%"
      justify="space-between"
    >
      <Tooltip
        label={subtext}
        hasArrow
        placement="top"
        borderRadius="8px"
        fontSize="12px"
        px={2}
        py={1}
      >
        <Flex
          align="center"
          justify="center"
          position="absolute"
          top={3}
          right={3}
          w="16px"
          h="16px"
          flexShrink={0}
        >
          <ButtonIcon
            size="small"
            ariaLabel="help text"
            icon={<FontAwesomeIcon icon={faInfoCircle} />}
            data-cy="info-icon"
          />
        </Flex>
      </Tooltip>
      <Flex align="start" pr={5} minH={{ base: '48px', md: '44px' }}>
        <Text
          textStyle="textBody"
          textTransform="uppercase"
          fontSize={{ base: '10px', md: '11px' }}
          noOfLines={{ base: 3, md: 2 }}
        >
          {label}
        </Text>
      </Flex>
      <Text
        textStyle="textHeading"
        fontSize={{ base: '2xl', md: '3xl' }}
        fontWeight="700"
        lineHeight="1"
      >
        {value}
      </Text>
    </Flex>
  </OutlineCard>
);

const JournalCodeBadge = ({ journalCode }: { journalCode?: string }) => {
  const journalCodes = (journalCode ?? '')
    .split(',')
    .map(code => code.trim())
    .filter(Boolean)
    .map(code => code.toUpperCase());
  const visibleCodes = journalCodes.slice(0, 5);
  const hiddenCodes = journalCodes.slice(5);

  if (!visibleCodes.length) {
    return <Badge variant="neutral">All Journals</Badge>;
  }

  const badge = (
    <Badge variant="neutral">
      {visibleCodes.join(',')}
      {hiddenCodes.length ? ', ...' : ''}
    </Badge>
  );

  return hiddenCodes.length ? (
    <Tooltip label={hiddenCodes.join(', ')} hasArrow placement="top">
      <Box display="inline-block">{badge}</Box>
    </Tooltip>
  ) : (
    badge
  );
};

const AnalyticsPanel = ({
  title,
  eyebrow,
  meta,
  children,
  workspaceLabel,
  action
}: {
  title: string;
  eyebrow?: string;
  meta?: string;
  children: ReactNode;
  workspaceLabel?: ReactNode;
  action?: ReactNode;
}) => (
  <OutlineCard p={{ base: 4, md: 5 }} minH="100%">
    <Flex
      justify="space-between"
      gap={{ base: 3, md: 4 }}
      align={{ base: 'start', md: 'start' }}
      direction={{ base: 'column', md: 'row' }}
      mb={5}
    >
      <Box>
        {eyebrow ? (
          <Text
            color="#718096"
            fontSize="11px"
            fontWeight="700"
            letterSpacing="0.18em"
            textTransform="uppercase"
            mb={2}
          >
            {eyebrow}
          </Text>
        ) : null}
        <Text color="#1a202c" fontSize="md" fontWeight="700">
          {title}
        </Text>
      </Box>
      <Flex
        align={{ base: 'start', md: 'center' }}
        gap={2}
        wrap="wrap"
        justify={{ base: 'start', md: 'end' }}
        w={{ base: '100%', md: 'auto' }}
      >
        {meta ? (
          <Text color="#718096" fontSize="xs">
            {meta}
          </Text>
        ) : null}
        {workspaceLabel ? workspaceLabel : null}
        {action}
      </Flex>
    </Flex>
    {children}
  </OutlineCard>
);

const UnauthorizedAnalyticsState = () => (
  <OutlineCard p={{ base: 8, md: 12 }}>
    <Flex
      direction="column"
      align="center"
      justify="center"
      textAlign="center"
      minH="420px"
      gap={5}
    >
      <Flex
        align="center"
        justify="center"
        w="64px"
        h="64px"
        borderRadius="999px"
        bg="#EEF2FF"
        color="#3730A3"
      >
        <FontAwesomeIcon icon={faLockKeyhole} size="2x" />
      </Flex>
      <Box maxW="560px">
        <Text fontSize={{ base: '2xl', md: '3xl' }} fontWeight="700" mb={2}>
          You do not have access to analytics
        </Text>
        <Text color="#718096" fontSize="md">
          Unauthorized: User does not have access to perform this action.
          Contact your workspace administrator if you believe this is incorrect.
        </Text>
      </Box>
    </Flex>
  </OutlineCard>
);
export const transformRevisionData = (data: any[]) => {
  const allTypes = new Set<string>();

  data.forEach(item => {
    item.by_type.forEach((t: any) => {
      allTypes.add(t.type);
    });
  });

  const typesArray = Array.from(allTypes);

  const formatted = data.map(item => {
    const obj: any = {
      revision: item.revision,
      total: item.count
    };

    typesArray.forEach(type => {
      obj[type] = 0;
    });

    item.by_type.forEach((t: any) => {
      obj[t.type] = t.count;
    });

    return obj;
  });

  return { formatted, typesArray };
};

const renderChartByConfig = (
  chart: WorkspaceAnalyticsChartConfig,
  chartData: CleanAnalyticsResponse | null,
  requestValues?: RequestValues,
  showAllArticleRevisions = false
) => {
  switch (chart.chartType) {
    case 'bar':
      switch (chart.id) {
        case 'by_revison_round':
        case 'by_revision_round':
          return (
            <ManuscriptsReceivedChart
              data={getRevisionRoundBarData(chartData)}
              axes={chart.axes}
            />
          );
        case 'by_article_type':
          return (
            <ArticleTypeBarChart
              data={getArticleTypeBarData(chartData)}
              showAllRevisions={showAllArticleRevisions}
              axes={chart.axes}
            />
          );
        case 'submission_pipeline':
          return (
            <ManuscriptsReceivedChart
              data={getSubmissionPipelineBarData(chartData)}
              axes={chart.axes}
            />
          );
        default:
          return (
            <ArticleTypeBarChart
              data={articleTypeBarChartData}
              showAllRevisions={showAllArticleRevisions}
              axes={chart.axes}
            />
          );
      }
    case 'line':
      switch (chart.id) {
        case 'submission_over_time':
          return (
            <SubmissionOverTimeAreaChart
              data={getSubmissionOverTimeAreaData(chartData, requestValues)}
              axes={{
                ...chart.axes,
                x: getSubmissionOverTimeXAxisLabel(chartData, requestValues)
              }}
            />
          );
        default:
          return (
            <ManuscriptCountLineChart
              data={getManuscriptCountLineData(chartData)}
              axes={chart.axes}
            />
          );
      }
    case 'pie':
      return <DecisionRatioChart data={getDecisionRatioData(chartData)} />;
    case 'line_area':
    case 'line-area':
      switch (chart.id) {
        case 'submission_over_time':
          return (
            <SubmissionOverTimeAreaChart
              data={getSubmissionOverTimeAreaData(chartData, requestValues)}
              axes={{
                ...chart.axes,
                x: getSubmissionOverTimeXAxisLabel(chartData, requestValues)
              }}
            />
          );
        default:
          return (
            <RevisionRoundLineAreaChart
              data={getRevisionRoundLineAreaData(chartData)}
              axes={chart.axes}
            />
          );
      }

    default:
      return (
        <Text color="#718096" fontSize="sm">
          No chart renderer mapped for chart type {chart.chartType}.
        </Text>
      );
  }
};

const renderRequestField = (
  field: WorkspaceAnalyticsRequestField,
  value: string,
  requestValues: RequestValues,
  setValue: (key: string, nextValue: string) => void
) => {
  const sharedProps = {
    name: field.key,
    value
  };
  const today = new Date().toISOString().slice(0, 10);

  const dateRangeProps =
    field.key === 'startDate'
      ? {
          max:
            requestValues.endDate && requestValues.endDate < today
              ? requestValues.endDate
              : today
        }
      : field.key === 'endDate'
        ? {
            min: requestValues.startDate || undefined,
            max: today
          }
        : {};

  if (field.type === 'date') {
    return (
      <DatePicker
        {...sharedProps}
        {...dateRangeProps}
        onChange={event => setValue(field.key, event.target.value)}
      />
    );
  }

  if (
    field.type === 'string' &&
    field.options &&
    Array.isArray(field.options) &&
    field.options.length > 0
  ) {
    const selectedValues = value
      .split(',')
      .map(item => item.trim())
      .filter(Boolean);

    return (
      <MultiSelect
        options={field.options}
        value={selectedValues}
        onChange={selectedOptions =>
          setValue(field.key, selectedOptions.join(','))
        }
        placeholder={`Select ${field.name}`}
        variant="interactive"
      />
    );
  }

  return (
    <Input
      {...sharedProps}
      onChange={event => setValue(field.key, event.target.value)}
      placeholder={`Enter ${field.name}`}
    />
  );
};

const buildRequestSchemaValues = (
  config: WorkspaceAnalyticsTabConfig | undefined,
  requestValues: RequestValues
): AnalyticsRequestSchemaValues => {
  const entries =
    config?.requestSchema?.map((field: WorkspaceAnalyticsRequestField) => {
      const rawValue = requestValues[field.key] ?? '';

      if (
        field.type === 'string' &&
        Array.isArray(field.options) &&
        field.options.length > 0
      ) {
        return [
          field.key,
          rawValue
            .split(',')
            .map(item => item.trim())
            .filter(Boolean)
        ] as const;
      }

      return [field.key, rawValue] as const;
    }) ?? [];

  return Object.fromEntries(entries);
};

const getInitialRequestFieldValue = (field: WorkspaceAnalyticsRequestField) => {
  if (
    field.key === 'journalCode' &&
    Array.isArray(field.options) &&
    field.options.length > 0
  ) {
    return field.options.map(option => option.value).join(',');
  }

  return '';
};

const Analytics = () => {
  const { workspaceId } = useWorkspace();
  const toast = useToast();
  const { data: analyticsConfigResponse, isLoading: isLoadingAnalyticsConfig } =
    useQuery({
      queryKey: ['workspace-analytics-configs', { workspaceId }],
      queryFn: () =>
        workspaceId ? fetchWorkspaceAnalyticsConfigs(workspaceId) : null,
      enabled: !!workspaceId,
      onError: (error: any) => {
        const status = getErrorStatusCode(error);
        if (status === 401) {
          setIsUnauthorized(true);
        } else if (status === 403) {
          toast({
            title: 'Forbidden',
            description: 'Forbidden: You do not have access to this resource.',
            variant: 'attention'
          });
        }
      }
    });

  const tabConfigs = useMemo(
    () => normalizeAnalyticsConfigs(analyticsConfigResponse),
    [analyticsConfigResponse]
  );
  const [activeTabId, setActiveTabId] = useState<string>(
    tabConfigs[0]?.id ?? 'overview'
  );
  const [requestValues, setRequestValues] = useState<RequestValues>({});
  const [statsResponse, setStatsResponse] =
    useState<AnalyticsStatsResponse | null>(null);
  const [chartResponses, setChartResponses] = useState<
    Record<string, CleanAnalyticsResponse | null>
  >({});
  const [zoomedChartId, setZoomedChartId] = useState<string | null>(null);
  const [isUnauthorized, setIsUnauthorized] = useState(false);
  const [autoLoadedTabs, setAutoLoadedTabs] = useState<Record<string, boolean>>(
    {}
  );
  const {
    fetchWorkspaceAnalyticsOverviewAsync,
    isLoading: isLoadingAnalytics
  } = useWorkspaceAnalyticsOverviewMutation(workspaceId);

  useEffect(() => {
    if (!tabConfigs.some(config => config.id === activeTabId)) {
      setActiveTabId(tabConfigs[0]?.id ?? 'overview');
    }
  }, [activeTabId, tabConfigs]);

  useEffect(() => {
    setRequestValues(currentValues => {
      const nextValues = { ...currentValues };

      tabConfigs.forEach(config => {
        config.requestSchema?.forEach(field => {
          if (nextValues[field.key] === undefined) {
            nextValues[field.key] = getInitialRequestFieldValue(field);
          }
        });
      });

      return nextValues;
    });
  }, [tabConfigs]);

  const activeTabConfig =
    tabConfigs.find(config => config.id === activeTabId) ?? tabConfigs[0];

  const analyticsStats = statsResponse as AnalyticsStatsResponse | null;

  const dynamicSummaryMetrics = buildMetricCards(analyticsStats);

  const hasRequiredJournalCodeField = !!activeTabConfig?.requestSchema?.some(
    field => field.key === 'journalCode'
  );

  const zoomedChart = useMemo(
    () =>
      activeTabConfig?.charts?.find(chart => chart.id === zoomedChartId) ??
      null,
    [activeTabConfig, zoomedChartId]
  );

  const activeRequestSchemaValues = useMemo(
    () => buildRequestSchemaValues(activeTabConfig, requestValues),
    [activeTabConfig, requestValues]
  );

  const isActiveTabRequestSchemaInitialized = useMemo(() => {
    if (!activeTabConfig?.requestSchema?.length) {
      return true;
    }

    return activeTabConfig.requestSchema.every(
      field => requestValues[field.key] !== undefined
    );
  }, [activeTabConfig, requestValues]);

  const handleFieldChange = (key: string, value: string) => {
    setRequestValues(currentValues => ({
      ...currentValues,
      [key]: value,
      ...(key === 'startDate' &&
      value &&
      currentValues.endDate &&
      value > currentValues.endDate
        ? { endDate: value }
        : {}),
      ...(key === 'endDate' &&
      value &&
      currentValues.startDate &&
      value < currentValues.startDate
        ? { startDate: value }
        : {})
    }));
  };

  const clearAnalyticsData = () => {
    setStatsResponse(null);
    setChartResponses({});
  };

  const validateJournalCodeSelection = (
    requestSchemaValues: AnalyticsRequestSchemaValues,
    showToast = false
  ) => {
    if (!hasRequiredJournalCodeField) {
      return true;
    }

    const journalCodeValue = requestSchemaValues.journalCode;
    const hasSelection = Array.isArray(journalCodeValue)
      ? journalCodeValue.length > 0
      : typeof journalCodeValue === 'string'
        ? journalCodeValue.trim().length > 0
        : false;

    if (!hasSelection && showToast) {
      toast({
        title: 'Journal code is required',
        description: 'Select at least one journal code to load analytics.',
        variant: 'attention'
      });
    }

    return hasSelection;
  };

  const handleResetFilters = () => {
    if (!activeTabConfig?.requestSchema?.length) {
      void loadAnalyticsOverview();
      return;
    }

    const resetValues = activeTabConfig.requestSchema.reduce<RequestValues>(
      (accumulator, field) => {
        accumulator[field.key] = '';
        return accumulator;
      },
      {}
    );

    setRequestValues(currentValues => ({
      ...currentValues,
      ...resetValues
    }));

    const resetRequestSchemaValues = buildRequestSchemaValues(
      activeTabConfig,
      resetValues
    );

    if (!validateJournalCodeSelection(resetRequestSchemaValues)) {
      clearAnalyticsData();
      return;
    }

    void loadAnalyticsOverview(resetRequestSchemaValues);
  };

  const loadAnalyticsOverview = async (
    nextRequestSchemaValues?: AnalyticsRequestSchemaValues
  ) => {
    if (!workspaceId || !activeTabConfig) {
      return;
    }

    const requestSchemaValues =
      nextRequestSchemaValues ?? activeRequestSchemaValues;

    if (!validateJournalCodeSelection(requestSchemaValues)) {
      clearAnalyticsData();
      return;
    }

    setIsUnauthorized(false);

    if (activeTabConfig.id === 'overview') {
      const statsResult = await fetchWorkspaceAnalyticsOverviewAsync({
        analyticsType: 'stats',
        requestSchema: requestSchemaValues,
        statsId: 'dashboard_header_counts',
        selectedTabId: activeTabConfig.id
      });

      if (
        !statsResult.isSuccess &&
        getErrorStatusCode(statsResult.message) === 401
      ) {
        setIsUnauthorized(true);
        clearAnalyticsData();
        return;
      }

      if (statsResult.isSuccess) {
        setStatsResponse(
          unwrapAnalyticsPayload<AnalyticsStatsResponse>(statsResult)
        );
      }
    } else {
      setStatsResponse(null);
    }

    const chartResultEntries = await Promise.all(
      (activeTabConfig.charts ?? []).map(async chart => {
        const chartResult = await fetchWorkspaceAnalyticsOverviewAsync({
          analyticsType: 'chart',
          requestSchema: requestSchemaValues,
          chartId: chart.id,
          selectedTabId: activeTabConfig.id
        });

        if (
          !chartResult.isSuccess &&
          getErrorStatusCode(chartResult.message) === 401
        ) {
          setIsUnauthorized(true);
          return [chart.id, undefined] as const;
        }

        return [chart.id, chartResult] as const;
      })
    );

    setChartResponses(
      Object.fromEntries(
        chartResultEntries.map(([chartId, response]) => [
          chartId,
          response?.isSuccess
            ? unwrapAnalyticsPayload<CleanAnalyticsResponse>(response)
            : undefined
        ])
      )
    );
  };

  useEffect(() => {
    if (
      !workspaceId ||
      !activeTabConfig ||
      !isActiveTabRequestSchemaInitialized
    ) {
      return;
    }

    if (autoLoadedTabs[activeTabId]) {
      return;
    }

    setAutoLoadedTabs(currentState => ({
      ...currentState,
      [activeTabId]: true
    }));

    void loadAnalyticsOverview(activeRequestSchemaValues);
  }, [
    activeRequestSchemaValues,
    activeTabConfig,
    activeTabId,
    autoLoadedTabs,
    isActiveTabRequestSchemaInitialized,
    workspaceId
  ]);

  const shouldShowChartZoom = (_chart: WorkspaceAnalyticsChartConfig) => true;

  if (isLoadingAnalyticsConfig) {
    return <Loading />;
  }

  return (
    <Box id="scrollable-wrapper" overflowY="auto" height="100%">
      <Box minH="100%">
        <Box
          maxW="1440px"
          mx="auto"
          px={{ base: 4, md: 6 }}
          py={2}
          position="relative"
        >
          {isUnauthorized ? (
            <UnauthorizedAnalyticsState />
          ) : (
            <>
              <Flex
                justify="space-between"
                align={{ base: 'start', lg: 'center' }}
                gap={4}
                direction={{ base: 'column', lg: 'row' }}
              >
                <Box w="100%" overflowX="auto" pb={1}>
                  <SingleItem.Navigation
                    size="compact"
                    bgColor="neutralPrimaryBorder"
                  >
                    {tabConfigs.map(tab => (
                      <SingleItem.Tab
                        key={tab.id}
                        isActive={activeTabId === tab.id}
                        onClick={() => setActiveTabId(tab.id)}
                      >
                        {tab.tabName}
                      </SingleItem.Tab>
                    ))}
                  </SingleItem.Navigation>
                </Box>
              </Flex>
              {activeTabConfig?.requestSchema?.length ? (
                <SimpleGrid
                  columns={{ base: 1, md: 2, xl: 4 }}
                  spacing={4}
                  pt={4}
                  alignItems="start"
                >
                  {activeTabConfig.requestSchema.map(field => (
                    <Box key={field.key}>
                      <FieldLabel
                        label={field.name}
                        subText=""
                        isRequired={field.key === 'journalCode'}
                      />
                      {renderRequestField(
                        field,
                        requestValues[field.key] ?? '',
                        requestValues,
                        handleFieldChange
                      )}
                    </Box>
                  ))}
                  <Flex
                    align="start"
                    gap={2}
                    pt="28px"
                    flexShrink={0}
                    direction={{ base: 'column', sm: 'row' }}
                  >
                    <Button
                      variant="primary"
                      isLoading={isLoadingAnalytics}
                      w={{ base: '100%', sm: 'auto' }}
                      onClick={() => {
                        if (
                          !validateJournalCodeSelection(
                            activeRequestSchemaValues,
                            true
                          )
                        ) {
                          return;
                        }
                        void loadAnalyticsOverview();
                      }}
                    >
                      Apply
                    </Button>
                    <Button
                      variant="secondary"
                      disabled={isLoadingAnalytics}
                      w={{ base: '100%', sm: 'auto' }}
                      onClick={handleResetFilters}
                    >
                      Reset
                    </Button>
                  </Flex>
                </SimpleGrid>
              ) : null}
              <Box my={4}>
                {activeTabId === 'overview' ? (
                  <SimpleGrid
                    columns={{ base: 1, sm: 2, lg: 4, xl: 8 }}
                    spacing={{ base: 3, md: 3 }}
                    mb={4}
                  >
                    {dynamicSummaryMetrics.map(metric => (
                      <AnalyticsMetricCard key={metric.label} {...metric} />
                    ))}
                  </SimpleGrid>
                ) : null}

                <Grid templateColumns={{ base: '1fr', xl: '1fr 1fr' }} gap={4}>
                  {(activeTabConfig?.charts ?? []).map(chart => (
                    <AnalyticsPanel
                      key={chart.id}
                      title={chart.name}
                      meta={
                        chart.id === 'submission_over_time'
                          ? getSubmissionOverTimeMeta(
                              chartResponses[chart.id],
                              requestValues
                            )
                          : undefined
                      }
                      workspaceLabel={
                        <JournalCodeBadge
                          journalCode={requestValues.journalCode}
                        />
                      }
                      action={
                        shouldShowChartZoom(chart) ? (
                          <ButtonIcon
                            size="small"
                            ariaLabel={`Zoom ${chart.name}`}
                            icon={<FontAwesomeIcon icon={faExpand} />}
                            onClick={() => setZoomedChartId(chart.id)}
                            data-cy={`zoom-${chart.id}`}
                          />
                        ) : undefined
                      }
                    >
                      {renderChartByConfig(
                        chart,
                        chartResponses[chart.id],
                        requestValues
                      )}
                    </AnalyticsPanel>
                  ))}
                </Grid>
              </Box>
            </>
          )}
          {isLoadingAnalytics && !isUnauthorized ? (
            <Flex
              position="absolute"
              inset={0}
              zIndex={10}
              align="center"
              justify="center"
              bg="rgba(255, 255, 255, 0.72)"
              backdropFilter="blur(2px)"
            >
              <Flex
                align="center"
                gap={3}
                px={5}
                py={3}
                bg="white"
                border="1px solid #E2E8F0"
                borderRadius="12px"
                boxShadow="0 12px 28px rgba(15, 23, 42, 0.08)"
              >
                <Spinner color="#1D4ED8" thickness="3px" />
                <Text fontSize="sm" fontWeight="600" color="#1A202C">
                  Loading analytics...
                </Text>
              </Flex>
            </Flex>
          ) : null}
        </Box>
        {zoomedChart && !isUnauthorized ? (
          <Modal
            isOpen={!!zoomedChart}
            onClose={() => setZoomedChartId(null)}
            width="min(1080px, calc(100vw - 24px))"
            closeButtonProps={{
              hidden: false
            }}
          >
            <Box p={{ base: 4, md: 6 }}>
              <Flex
                justify="space-between"
                align={{ base: 'start', md: 'center' }}
                direction={{ base: 'column', md: 'row' }}
                mb={5}
                gap={4}
                pr={{ base: 0, md: 8 }}
              >
                <Text fontSize="lg" fontWeight="700">
                  {zoomedChart.name}
                </Text>
                <JournalCodeBadge journalCode={requestValues.journalCode} />
              </Flex>
              {zoomedChart.id === 'submission_over_time' ? (
                <SubmissionOverTimeAreaChart
                  data={getSubmissionOverTimeAreaData(
                    chartResponses[zoomedChart.id],
                    requestValues
                  )}
                  axes={{
                    ...zoomedChart.axes,
                    x: getSubmissionOverTimeXAxisLabel(
                      chartResponses[zoomedChart.id],
                      requestValues
                    )
                  }}
                  height={420}
                  showAllTicks
                />
              ) : (
                renderChartByConfig(
                  zoomedChart,
                  chartResponses[zoomedChart.id],
                  requestValues,
                  true
                )
              )}
            </Box>
          </Modal>
        ) : null}
        <Footer />
      </Box>
    </Box>
  );
};

export default Analytics;
